import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getDefaultRoute } from '../utils/defaultRoute';
import '../style/login.css';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [loginStep, setLoginStep] = useState('credentials'); // "credentials" or "verify"
  const [codeSent, setCodeSent] = useState(false);
  const [codeSending, setCodeSending] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [loading, setLoading] = useState(false);
  const [maskedTarget, setMaskedTarget] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [mode, setMode] = useState('login'); // 'login' or 'forgot'
  const [forgotStep, setForgotStep] = useState('email'); // 'email', 'reset', 'done'
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const sendCode = async () => {
    if (codeSending || countdown > 0) return;
    setCodeSending(true);
    setError(null);

    try {
      const res = await fetch('/api/auth/send-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      });
      const data = await res.json();

      if (res.ok && data.success) {
        setCodeSent(true);
        setCountdown(60);
        if (data.maskedTarget) setMaskedTarget(data.maskedTarget);
        setSuccess('Verification code sent via SMS');
        setTimeout(() => setSuccess(null), 3000);
        return true;
      } else {
        setError(data.message || 'Failed to send verification code');
        return false;
      }
    } catch {
      setError('Connection error. Please try again.');
      return false;
    } finally {
      setCodeSending(false);
    }
  };

  const handleSignIn = async () => {
    if (loading) return;
    setError(null);

    if (loginStep === 'credentials') {
      if (!email) { setError('Please enter your email address.'); return; }
      if (!password) { setError('Please enter your password.'); return; }

      setLoading(true);
      const sent = await sendCode();
      if (sent) setLoginStep('verify');
      setLoading(false);
    } else {
      if (!verificationCode || verificationCode.length !== 6) {
        setError('Please enter the 6-digit verification code.');
        return;
      }

      setLoading(true);
      try {
        const res = await fetch('/api/auth/login', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            email: email.trim(),
            password: password.trim(),
            code: verificationCode.trim(),
            rememberMe,
          }),
        });
        const data = await res.json();

        if (res.ok && data.success) {
          localStorage.setItem('token', data.token);
          localStorage.setItem('userId', data.id);
          if (data.username) localStorage.setItem('username', data.username);
          if (data.roleType) localStorage.setItem('roleType', data.roleType);
          if (data.permissions) localStorage.setItem('permissions', JSON.stringify(data.permissions));

          // Determine landing page based on permissions
          const permissions = data.permissions || [];
          const landingPage = getDefaultRoute(permissions, data.roleType || '');
          navigate(landingPage, { replace: true });
        } else {
          setError(data.message || 'Invalid credentials or verification code');
          // Reset to credentials step on login failure so user can try again
          setLoginStep('credentials');
          setVerificationCode('');
          setCodeSent(false);
          setCountdown(0);
        }
      } catch {
        setError('Connection error. Please try again.');
        // Reset to credentials step on connection error
        setLoginStep('credentials');
        setVerificationCode('');
        setCodeSent(false);
        setCountdown(0);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleForgotPassword = async () => {
    if (loading) return;
    setError(null);

    if (forgotStep === 'email') {
      if (!email) { setError('Please enter your email address.'); return; }
      setLoading(true);
      const sent = await sendCode();
      if (sent) setForgotStep('reset');
      setLoading(false);
    } else if (forgotStep === 'reset') {
      if (!verificationCode || verificationCode.length !== 6) {
        setError('Please enter the 6-digit verification code.'); return;
      }
      if (!newPassword) { setError('Please enter a new password.'); return; }
      if (newPassword.length < 8) { setError('Password must be at least 8 characters.'); return; }
      if (newPassword !== confirmPassword) { setError('Passwords do not match.'); return; }

      setLoading(true);
      try {
        const res = await fetch('/api/auth/reset-password', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: email.trim(),
            code: verificationCode.trim(),
            newPassword: newPassword.trim(),
          }),
        });
        const data = await res.json();

        if (res.ok && data.success) {
          setForgotStep('done');
          setSuccess(data.message);
        } else {
          setError(data.message || 'Failed to reset password');
        }
      } catch {
        setError('Connection error. Please try again.');
      } finally {
        setLoading(false);
      }
    }
  };

  const switchToForgot = () => {
    setMode('forgot');
    setForgotStep('email');
    setError(null);
    setSuccess(null);
    setVerificationCode('');
    setNewPassword('');
    setConfirmPassword('');
    setCodeSent(false);
    setCodeSending(false);
    setCountdown(0);
    setLoading(false);
    setMaskedTarget('');
  };

  const switchToLogin = () => {
    setMode('login');
    setLoginStep('credentials');
    setError(null);
    setSuccess(null);
    setVerificationCode('');
    setNewPassword('');
    setConfirmPassword('');
    setCodeSent(false);
    setCodeSending(false);
    setCountdown(0);
    setLoading(false);
    setMaskedTarget('');
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      if (mode === 'login') handleSignIn();
      else handleForgotPassword();
    }
  };

  // --- Forgot password UI ---
  const renderForgot = () => {
    if (forgotStep === 'done') {
      return (
        <>
          <h1 className="auth-title">Password Reset</h1>
          <div className="auth-success">Your password has been reset successfully.</div>
          <button className="btn-primary" onClick={switchToLogin} type="button">
            Back to Sign In
          </button>
        </>
      );
    }

    return (
      <>
        <h1 className="auth-title">
          {forgotStep === 'email' ? 'Forgot Password' : 'Reset Password'}
        </h1>
        <p className="auth-subtitle">
          {forgotStep === 'email'
            ? 'Enter your email to receive a verification code via SMS'
            : 'Enter the code sent to your phone and your new password'}
        </p>

        {forgotStep === 'email' ? (
          <div className="form-field">
            <label htmlFor="forgot-email" className="label">Email</label>
            <input
              id="forgot-email"
              type="email"
              placeholder="you@powow.com.au"
              className="input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyPress={handleKeyPress}
              autoComplete="username"
            />
          </div>
        ) : (
          <>
            <div className="email-display">
              <span className="email-label">Code sent to:</span>
              <span className="email-value">{maskedTarget || email}</span>
            </div>
            <div className="form-field">
              <label htmlFor="forgot-code" className="label">Verification Code</label>
              <div className="code-input-wrapper">
                <input
                  id="forgot-code"
                  type="text"
                  placeholder="Enter 6-digit code"
                  className="input code-input"
                  maxLength={6}
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                  onKeyPress={handleKeyPress}
                  autoComplete="one-time-code"
                />
                <button
                  type="button"
                  className="resend-btn"
                  disabled={countdown > 0 || codeSending}
                  onClick={sendCode}
                >
                  {countdown > 0 ? `${countdown}s` : codeSending ? 'Sending...' : 'Resend'}
                </button>
              </div>
            </div>
            <div className="form-field">
              <label htmlFor="new-password" className="label">New Password</label>
              <input
                id="new-password"
                type="password"
                placeholder="At least 8 characters"
                className="input"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                onKeyPress={handleKeyPress}
                autoComplete="new-password"
              />
            </div>
            <div className="form-field">
              <label htmlFor="confirm-password" className="label">Confirm Password</label>
              <input
                id="confirm-password"
                type="password"
                placeholder="Repeat new password"
                className="input"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                onKeyPress={handleKeyPress}
                autoComplete="new-password"
              />
            </div>
          </>
        )}

        {error && <div className="auth-error">{error}</div>}
        {success && <div className="auth-success">{success}</div>}

        <button
          className="btn-primary"
          disabled={loading}
          onClick={handleForgotPassword}
          type="button"
        >
          {loading ? (
            <span className="btn-loading">
              <span className="spinner" />
              {forgotStep === 'email' ? 'Sending...' : 'Resetting...'}
            </span>
          ) : forgotStep === 'email' ? 'Send Code' : 'Reset Password'}
        </button>

        <div className="forgot-link-row">
          <button type="button" className="link-btn" onClick={switchToLogin}>
            Back to Sign In
          </button>
        </div>
      </>
    );
  };

  // --- Login UI ---
  const renderLogin = () => (
    <>
      <h1 className="auth-title">
        {loginStep === 'credentials' ? 'Sign In' : 'Verify Identity'}
      </h1>
      <p className="auth-subtitle">
        {loginStep === 'credentials'
          ? 'Enter your credentials to access the dashboard'
          : 'Enter the verification code sent to your phone'}
      </p>

      {loginStep === 'credentials' ? (
        <>
          <div className="form-field">
            <label htmlFor="email" className="label">Email</label>
            <input
              id="email"
              type="email"
              placeholder="you@powow.com.au"
              className="input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyPress={handleKeyPress}
              autoComplete="username"
            />
          </div>
          <div className="form-field">
            <label htmlFor="password" className="label">Password</label>
            <div className="password-wrapper">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                placeholder="Enter your password"
                className="input password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyPress={handleKeyPress}
                autoComplete="current-password"
              />
              <button
                type="button"
                className="toggle-visibility"
                onClick={() => setShowPassword((v) => !v)}
              >
                {showPassword ? 'Hide' : 'Show'}
              </button>
            </div>
          </div>
          <div className="login-options-row">
            <label className="remember-me">
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
              />
              <span>30 days auto-login</span>
            </label>
            <button type="button" className="link-btn" onClick={switchToForgot}>
              Forgot password?
            </button>
          </div>
        </>
      ) : (
        <>
          <div className="email-display">
            <span className="email-label">Code sent to:</span>
            <span className="email-value">{maskedTarget || email}</span>
            <button type="button" className="link-btn" onClick={() => {
              setLoginStep('credentials');
              setVerificationCode('');
              setCodeSent(false);
              setCountdown(0);
              setError(null);
            }}>
              Change
            </button>
          </div>
          <div className="form-field">
            <label htmlFor="verificationCode" className="label">Verification Code</label>
            <div className="code-input-wrapper">
              <input
                id="verificationCode"
                type="text"
                placeholder="Enter 6-digit code"
                className="input code-input"
                maxLength={6}
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                onKeyPress={handleKeyPress}
                autoComplete="one-time-code"
              />
              <button
                type="button"
                className="resend-btn"
                disabled={countdown > 0 || codeSending}
                onClick={sendCode}
              >
                {countdown > 0 ? `${countdown}s` : codeSending ? 'Sending...' : 'Resend'}
              </button>
            </div>
          </div>
        </>
      )}

      {error && <div className="auth-error">{error}</div>}
      {success && <div className="auth-success">{success}</div>}

      <button
        className="btn-primary"
        disabled={loading}
        onClick={handleSignIn}
        type="button"
      >
        {loading ? (
          <span className="btn-loading">
            <span className="spinner" />
            {loginStep === 'credentials' ? 'Verifying...' : 'Signing in...'}
          </span>
        ) : loginStep === 'credentials' ? 'Continue' : 'Sign In'}
      </button>
    </>
  );

  return (
    <div className="login-page">
      <section className="login-left">
        <div className="login-left-content">
          <img src="/logocompany.png" alt="Powow Group" className="login-logo" />
          <h1 className="login-hero-title" style={{ color: '#fff', fontSize: 36 }}>Powow Group</h1>
          <h2 className="login-hero-title" style={{ color: '#54A2CC' }}>Management Dashboard</h2>
          <p className="login-hero-subtitle">
            Consolidated business intelligence across all Powow operations.
          </p>
        </div>
      </section>

      <section className="login-right">
        <div className="login-card">
          <div className="auth-form">
            {mode === 'login' ? renderLogin() : renderForgot()}
          </div>
        </div>
      </section>
    </div>
  );
}
